import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation, useOutletContext, useParams } from "react-router";
import { useAuth, useCan } from "../../auth/AuthProvider";
import { CopyField } from "../../components/CopyField";
import { DetailHeader } from "../../components/DetailHeader";
import { Icon } from "../../components/icons";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  LoadingBlock,
  Mono,
  Select,
  StatusBadge,
  Switch,
  Table,
  Td,
  Th,
  cx,
} from "../../components/ui";
import { absoluteDay, relativeTime } from "../../lib/format";
import { formatMoney } from "../../lib/money";
import {
  useBilling,
  useDomainAuthCode,
  useDomainTlds,
  useRegisteredDomain,
  useRenewDomain,
  useReplaceZoneRecords,
  useSyncDomain,
  useUpdateDomain,
  useZoneRecords,
} from "../../lib/queries";
import type { RegisteredDomainDetail, ZoneRecord, ZoneRecordType } from "../../lib/types";
import { domainStatus } from "./status";

const RECORD_TYPES: ZoneRecordType[] = ["A", "AAAA", "CNAME", "MX", "TXT", "NS", "SRV", "CAA"];

/**
 * One domain: what it is, where it points, and the two things that keep it
 * — renewal and the zone.
 *
 * Point-at-app leads, because it is why most people bought the name. The
 * record editor is under it for the rest; settings and nameservers below
 * that, because they are set once; the transfer code last, behind a click,
 * because reading it is a step towards leaving.
 */
const TABS = [
  { to: ".", key: "overview", end: true },
  { to: "dns", key: "dns" },
  { to: "settings", key: "settings" },
  { to: "orders", key: "orders" },
] as const;

/** What every tab reads: the domain, and who is looking. */
export interface DomainTabContext {
  orgId: string;
  domain: RegisteredDomainDetail;
  currency: string;
  canManage: boolean;
  canDeploy: boolean;
}

export const useDomainTab = () => useOutletContext<DomainTabContext>();

/**
 * One domain, in four tabs.
 *
 * Overview is what somebody comes back for: where the name is in its year,
 * and where it points. DNS is the zone and the nameservers, which are one
 * subject. Settings holds what is set once and the transfer code, behind a
 * click, because reading it is a step towards leaving. Orders is the money.
 * A page that held all four in a scroll made the customer pass the record
 * editor to reach a switch.
 */
export function DomainDetailPage() {
  const { t } = useTranslation("domains");
  const { domainId } = useParams<{ domainId: string }>();
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id ?? "";
  const canManage = useCan("admin");
  const canDeploy = useCan("developer");

  const domain = useRegisteredDomain(orgId || undefined, domainId);
  const billing = useBilling(orgId || undefined);
  const currency = billing.data?.currency ?? "USD";
  const sync = useSyncDomain(orgId, domainId ?? "");

  const location = useLocation();
  const notice = (location.state as { notice?: string } | null)?.notice ?? null;

  if (domain.isPending) return <LoadingBlock />;
  if (domain.error || !domain.data) return <Alert>{domain.error?.message ?? t("status.deleted.hint")}</Alert>;

  const row = domain.data;
  const status = domainStatus(row.status);
  const active = row.status === "active";
  const context: DomainTabContext = { orgId, domain: row, currency, canManage, canDeploy };

  return (
    <div className="space-y-6">
      <DetailHeader
        backTo="/domains"
        backLabel={t("back")}
        name={row.domain}
        badge={
          <StatusBadge tone={status.tone} label={t(`status.${status.key}.label`)} title={t(`status.${status.key}.hint`)} />
        }
        address={{ value: row.domain, href: active ? `https://${row.domain}` : undefined, copyLabel: t("detail.copy") }}
        actions={
          <Button variant="outline" size="sm" loading={sync.isPending} onClick={() => sync.mutate()}>
            <Icon name="refresh" className="size-3.5" />
            {t("detail.refresh")}
          </Button>
        }
        meta={[
          { label: t("detail.registered"), value: row.registeredAt ? absoluteDay(row.registeredAt) : "—" },
          { label: t("detail.renewsBy"), value: row.renewalDate ? absoluteDay(row.renewalDate) : "—" },
          {
            label: t("detail.pointsAt"),
            value: row.pointing.app ? (
              <Link to={`/apps/${row.pointing.app.id}`} className="text-fg hover:underline">
                {row.pointing.app.name}
              </Link>
            ) : (
              <span className="text-fg-subtle">—</span>
            ),
          },
        ]}
        tabs={active ? TABS.map((tab) => ({ ...tab, label: t(`detail.tabs.${tab.key}`) })) : undefined}
      />

      {notice && <Alert tone="success">{notice}</Alert>}
      {row.status === "pending" && <Alert tone="info">{t("detail.pending")}</Alert>}
      {row.status === "failed" && <Alert>{t("detail.failed", { reason: row.failureReason ?? "" })}</Alert>}
      {row.status === "expired" && <Alert>{t("detail.expired")}</Alert>}
      {active && row.renewalError && <Alert tone="warning">{t("detail.renewalError", { error: row.renewalError })}</Alert>}

      {active ? <Outlet context={context} /> : <Orders domain={row} currency={currency} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The year
// ---------------------------------------------------------------------------

/**
 * Where the name is in its registration: a bar from the day it was
 * registered to the day it must renew by, today marked on it, and the days
 * left as the one number on the page somebody comes back to read. The bar
 * turns amber inside the thirty days the sweep works in, and red when the
 * renewal cannot happen on its own — autorenew off, or the balance short.
 */
export function LifeStrip({ domain, action }: { domain: RegisteredDomainDetail; action?: ReactNode }) {
  const { t } = useTranslation("domains");
  if (!domain.renewalDate) return null;
  const start = domain.registeredAt ? Date.parse(domain.registeredAt) : Date.parse(domain.renewalDate) - 365 * DAY;
  const end = Date.parse(domain.renewalDate);
  const now = Date.now();
  const fraction = Math.min(1, Math.max(0, (now - start) / Math.max(1, end - start)));
  const daysLeft = Math.max(0, Math.ceil((end - now) / DAY));
  const atRisk = daysLeft <= 30 && (!domain.autorenew || domain.renewalError !== null);
  const soon = daysLeft <= 30;
  const tone = atRisk ? "bg-danger" : soon ? "bg-warn" : "bg-mint";

  return (
    <Card>
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 px-5 pt-4">
        <div>
          <p className="micro text-fg-subtle">{t("detail.life.label")}</p>
          <p className="mt-1 text-[13px] text-fg-muted">
            {atRisk
              ? t(domain.autorenew ? "detail.life.short" : "detail.life.manual")
              : domain.autorenew
                ? t("detail.life.auto", { date: absoluteDay(domain.renewalDate) })
                : t("detail.life.manual")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          {action}
          <p className="flex items-baseline gap-1.5">
            <span className={cx("text-3xl tabular-nums tracking-[-0.02em]", atRisk ? "text-danger" : "text-fg")}>{daysLeft}</span>
            <span className="text-[13px] text-fg-muted">{t("detail.life.daysLeft", { count: daysLeft })}</span>
          </p>
        </div>
      </div>
      <div className="px-5 pt-3 pb-4">
        <div className="relative h-1.5 overflow-hidden rounded-full bg-raised">
          <div className={cx("absolute inset-y-0 left-0 rounded-full transition-[width] duration-700 ease-smooth", tone)} style={{ width: `${fraction * 100}%` }} />
        </div>
        <div className="mt-2 flex justify-between font-mono text-[11px] text-fg-subtle">
          <span>{domain.registeredAt ? absoluteDay(domain.registeredAt) : "—"}</span>
          <span>{absoluteDay(domain.renewalDate)}</span>
        </div>
      </div>
    </Card>
  );
}

const DAY = 86_400_000;

// ---------------------------------------------------------------------------
// The zone
// ---------------------------------------------------------------------------

type Draft = ZoneRecord & { key: number };

let nextKey = 1;
const withKeys = (records: ZoneRecord[]): Draft[] => records.map((record) => ({ ...record, key: nextKey++ }));

export function Records({ orgId, domain, canEdit }: { orgId: string; domain: RegisteredDomainDetail; canEdit: boolean }) {
  const { t } = useTranslation("domains");
  const records = useZoneRecords(orgId, domain.id);
  const replace = useReplaceZoneRecords(orgId, domain.id);
  const serverRecords = records.data?.data;
  // Seeded at construction, not only in the effect: a warm query would
  // otherwise paint an empty editor for one frame — and in a static render,
  // for good. The effect below takes the later arrivals: first load when
  // cold, after a save, and after point-at-app rewrites the zone.
  const [draft, setDraft] = useState<Draft[] | null>(() => (serverRecords ? withKeys(serverRecords) : null));
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (serverRecords) setDraft(withKeys(serverRecords));
  }, [serverRecords]);

  const rows = draft ?? [];
  const dirty = draft !== null && JSON.stringify(rows.map(strip)) !== JSON.stringify(records.data?.data ?? []);

  const update = (key: number, patch: Partial<ZoneRecord>) =>
    setDraft(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  const add = () => setDraft([...rows, { key: nextKey++, name: "", type: "A", value: "", ttl: 3600 }]);
  const remove = (key: number) => setDraft(rows.filter((row) => row.key !== key));

  const save = (event: FormEvent) => {
    event.preventDefault();
    setSaved(false);
    replace.mutate(rows.map(strip), { onSuccess: () => setSaved(true) });
  };

  return (
    <Card>
      <CardHeader
        title={t("detail.sections.records")}
        description={t("detail.records.description", { domain: domain.domain })}
        action={
          canEdit ? (
            <Button variant="outline" size="sm" onClick={add}>
              <Icon name="plus" className="size-3.5" />
              {t("detail.records.add")}
            </Button>
          ) : undefined
        }
      />
      {records.error && (
        <div className="px-5 py-4">
          <Alert>{records.error.message}</Alert>
        </div>
      )}
      {records.isPending ? (
        <LoadingBlock />
      ) : rows.length === 0 ? (
        <p className="px-5 py-4 text-sm text-fg-muted">{t("detail.records.empty")}</p>
      ) : (
        <form onSubmit={save}>
          {/*
            A grid rather than a table: every cell is an input, and a table of
            inputs draws its own borders around theirs. One hairline per row,
            mono for the name and the value, the type as a short select.
          */}
          <div className="hidden grid-cols-[9rem_6.5rem_1fr_5.5rem_5rem_auto] gap-3 border-b border-line px-5 py-2 md:grid">
            {[t("detail.records.name"), t("detail.records.type"), t("detail.records.value"), t("detail.records.ttl"), t("detail.records.priority"), ""].map((label, i) => (
              <span key={i} className="micro text-fg-subtle">{label}</span>
            ))}
          </div>
          <ul>
            {rows.map((row) => (
              <li key={row.key} className="grid grid-cols-2 items-center gap-3 border-b border-line px-5 py-2.5 md:grid-cols-[9rem_6.5rem_1fr_5.5rem_5rem_auto]">
                {canEdit ? (
                  <>
                    <Input value={row.name} placeholder="@" onChange={(e) => update(row.key, { name: e.target.value })} className="font-mono" spellCheck={false} />
                    <Select value={row.type} onChange={(e) => update(row.key, { type: e.target.value as ZoneRecordType })}>
                      {RECORD_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </Select>
                    <Input value={row.value} onChange={(e) => update(row.key, { value: e.target.value })} className="col-span-2 font-mono md:col-span-1" spellCheck={false} />
                    <Input type="number" min={60} max={86400} value={row.ttl} onChange={(e) => update(row.key, { ttl: Number(e.target.value) })} />
                    {row.type === "MX" || row.type === "SRV" ? (
                      <Input type="number" min={0} max={65535} value={row.prio ?? ""} placeholder="10" onChange={(e) => update(row.key, { prio: e.target.value === "" ? undefined : Number(e.target.value) })} />
                    ) : (
                      <span className="hidden text-center text-fg-subtle md:block">—</span>
                    )}
                    <Button type="button" variant="ghost" size="sm" onClick={() => remove(row.key)} aria-label={t("detail.records.remove")} className="col-span-2 justify-self-end md:col-span-1">
                      <Icon name="trash" className="size-3.5" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Mono className="text-[13px]">{row.name || "@"}</Mono>
                    <span className="text-[13px] text-fg-muted">{row.type}</span>
                    <Mono className="col-span-2 break-all text-[13px] md:col-span-1">{row.value}</Mono>
                    <span className="text-[13px] tabular-nums text-fg-muted">{row.ttl}</span>
                    <span className="text-[13px] tabular-nums text-fg-muted">{row.prio ?? "—"}</span>
                    <span />
                  </>
                )}
              </li>
            ))}
          </ul>
          {canEdit && (
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
              <span className={cx("text-[12px]", dirty ? "text-warn" : "text-fg-subtle")}>
                {dirty ? t("detail.records.unsaved") : saved ? t("detail.records.saved") : ""}
              </span>
              <div className="flex items-center gap-3">
                {replace.error && <span className="text-[12px] text-danger">{replace.error.message}</span>}
                <Button type="submit" size="sm" disabled={!dirty} loading={replace.isPending}>
                  {t("detail.records.save")}
                </Button>
              </div>
            </div>
          )}
        </form>
      )}
      {rows.length === 0 && canEdit && replace.error && (
        <div className="px-5 pb-4">
          <Alert>{replace.error.message}</Alert>
        </div>
      )}
    </Card>
  );
}

/** The record without the editor's row key, which the API has no field for. */
function strip(row: Draft): ZoneRecord {
  const { key: _key, ...record } = row;
  void _key;
  return record;
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export function Settings({ orgId, domain, currency }: { orgId: string; domain: RegisteredDomainDetail; currency: string }) {
  const { t } = useTranslation("domains");
  const update = useUpdateDomain(orgId, domain.id);
  const renew = useRenewDomain(orgId, domain.id);
  const tlds = useDomainTlds(orgId);
  const tld = tlds.data?.data.find((row) => row.extension === domain.extension);
  const [years, setYears] = useState(1);
  const renewPrice = tld?.renewMicros === null || tld?.renewMicros === undefined ? null : tld.renewMicros * years;

  // Autorenew is on the overview's life strip, beside what it decides.
  const toggles: { key: "privateWhois" | "locked"; label: string; hint: string; disabled?: boolean }[] = [
    {
      key: "privateWhois",
      label: t("detail.settings.privacy"),
      hint: tld && !tld.privateWhoisAllowed ? t("new.buy.privacyUnavailable") : t("detail.settings.privacyHint"),
      disabled: tld ? !tld.privateWhoisAllowed : false,
    },
    { key: "locked", label: t("detail.settings.lock"), hint: t("detail.settings.lockHint") },
  ];

  return (
    <Card>
      <CardHeader title={t("detail.sections.settings")} />
      <div className="divide-y divide-line">
        {toggles.map((toggle) => (
          <div key={toggle.key} className="flex items-start justify-between gap-4 px-5 py-4">
            <div>
              <p className="text-[13px] font-medium text-fg">{toggle.label}</p>
              <p className="mt-0.5 text-[12px] leading-5 text-fg-subtle">{toggle.hint}</p>
            </div>
            <Switch
              checked={domain[toggle.key]}
              disabled={toggle.disabled || update.isPending}
              onChange={(next) => update.mutate({ [toggle.key]: next })}
              label={toggle.label}
            />
          </div>
        ))}
        <div className="flex flex-wrap items-end justify-between gap-3 px-5 py-4">
          <div>
            <p className="text-[13px] font-medium text-fg">{t("detail.settings.renew")}</p>
            {renew.isSuccess && <p className="mt-0.5 text-[12px] text-mint">{t("detail.settings.renewed")}</p>}
            {renew.error && <p className="mt-0.5 text-[12px] text-danger">{renew.error.message}</p>}
            {update.error && <p className="mt-0.5 text-[12px] text-danger">{update.error.message}</p>}
          </div>
          <div className="flex items-center gap-2">
            <div className="w-32">
              <Select value={years} onChange={(e) => setYears(Number(e.target.value))}>
                {Array.from({ length: tld?.maxPeriod ?? 10 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {t("new.buy.year", { count: n })}
                  </option>
                ))}
              </Select>
            </div>
            <Button variant="outline" size="sm" loading={renew.isPending} disabled={renewPrice === null} onClick={() => renew.mutate(years)}>
              {t("detail.settings.renewFor", { price: renewPrice === null ? "—" : formatMoney(renewPrice, currency) })}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Nameservers
// ---------------------------------------------------------------------------

export function Nameservers({ orgId, domain }: { orgId: string; domain: RegisteredDomainDetail }) {
  const { t } = useTranslation("domains");
  const update = useUpdateDomain(orgId, domain.id);
  const [custom, setCustom] = useState(!domain.managedDns);
  const [servers, setServers] = useState<string[]>(domain.managedDns ? ["", ""] : [...domain.nameServers, ...Array(Math.max(0, 2 - domain.nameServers.length)).fill("")]);
  const [saved, setSaved] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setSaved(false);
    const list = custom ? servers.map((s) => s.trim()).filter(Boolean) : [];
    update.mutate({ nameServers: list }, { onSuccess: () => setSaved(true) });
  };

  return (
    <Card>
      <CardHeader title={t("detail.sections.nameservers")} description={t("detail.nameservers.description")} />
      <form onSubmit={submit} className="space-y-4 px-5 py-4">
        <div className="flex gap-1 rounded-md border border-line bg-raised p-1 sm:w-fit">
          {([false, true] as const).map((isCustom) => (
            <button
              key={String(isCustom)}
              type="button"
              onClick={() => setCustom(isCustom)}
              className={cx(
                "flex-1 rounded px-4 py-1.5 text-[13px] font-medium transition-colors sm:flex-none",
                custom === isCustom ? "bg-surface text-fg shadow-sm" : "text-fg-muted hover:text-fg",
              )}
            >
              {isCustom ? t("detail.nameservers.custom") : t("detail.nameservers.hosted")}
            </button>
          ))}
        </div>
        {custom && (
          <div className="grid gap-2 sm:grid-cols-2">
            {servers.map((server, i) => (
              <Input
                key={i}
                value={server}
                placeholder={t("detail.nameservers.placeholder").replace("ns1", `ns${i + 1}`)}
                onChange={(e) => setServers(servers.map((s, j) => (j === i ? e.target.value : s)))}
                className="font-mono"
              />
            ))}
            {servers.length < 6 && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setServers([...servers, ""])}>
                <Icon name="plus" className="size-3.5" />
              </Button>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          {/* Nothing to submit while the choice matches what is set. */}
          <Button type="submit" size="sm" loading={update.isPending} disabled={custom === !domain.managedDns && !custom}>
            {custom ? t("detail.nameservers.save") : t("detail.nameservers.useHosted")}
          </Button>
          {saved && <span className="text-[12px] text-mint">{t("detail.nameservers.saved")}</span>}
          {update.error && <span className="text-[12px] text-danger">{update.error.message}</span>}
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Transfer out
// ---------------------------------------------------------------------------

export function TransferOut({ orgId, domain }: { orgId: string; domain: RegisteredDomainDetail }) {
  const { t } = useTranslation("domains");
  const code = useDomainAuthCode(orgId, domain.id);
  const [password, setPassword] = useState("");
  // ICANN's sixty days from registration, counted here so the customer
  // reads a date rather than a refusal.
  const holdUntil = domain.registeredAt ? new Date(Date.parse(domain.registeredAt) + 60 * DAY) : null;
  const held = holdUntil !== null && holdUntil.getTime() > Date.now();

  return (
    <Card>
      <CardHeader title={t("detail.sections.transferOut")} description={t("detail.transferOut.description")} />
      <div className="space-y-3 px-5 py-4">
        {held ? (
          <p className="text-[13px] leading-6 text-fg-muted">{t("detail.transferOut.held", { date: absoluteDay(holdUntil.toISOString()) })}</p>
        ) : code.data ? (
          <>
            <CopyField value={code.data.data.authCode} label={t("new.transfer.authCode")} />
            <Alert tone="warning">{t("detail.transferOut.warning")}</Alert>
          </>
        ) : (
          <form
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              if (password) code.mutate(password, { onSettled: () => setPassword("") });
            }}
            className="flex flex-wrap items-end gap-2"
          >
            {domain.locked && <p className="basis-full text-[12px] leading-5 text-fg-subtle">{t("detail.transferOut.lockedFirst")}</p>}
            <div className="w-64">
              <Field label={t("detail.transferOut.password")} htmlFor="transfer-password">
                <Input id="transfer-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
              </Field>
            </div>
            <Button type="submit" variant="outline" size="sm" loading={code.isPending} disabled={!password}>
              {t("detail.transferOut.reveal")}
            </Button>
          </form>
        )}
        {code.error && <Alert>{code.error.message}</Alert>}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Orders
// ---------------------------------------------------------------------------

export function Orders({ domain, currency }: { domain: RegisteredDomainDetail; currency: string }) {
  const { t } = useTranslation("domains");
  if (domain.orders.length === 0) return null;
  return (
    <Card>
      <CardHeader title={t("detail.sections.orders")} />
      <Table
        head={
          <>
            <Th>{t("detail.sections.orders")}</Th>
            <Th>{t("columns.status")}</Th>
            <Th className="text-right">{t("new.buy.total")}</Th>
          </>
        }
      >
        {domain.orders.map((order) => (
          <tr key={order.id}>
            <Td>
              <div className="text-fg">
                {t(`detail.orders.kind.${order.kind}`)}
                {order.kind !== "restore" && <span className="text-fg-muted"> · {t("new.buy.year", { count: order.periodYears })}</span>}
                {order.automatic && <span className="text-fg-subtle"> · {t("detail.orders.automatic")}</span>}
              </div>
              <div className="text-[11px] text-fg-subtle">{relativeTime(order.createdAt)}</div>
            </Td>
            <Td className={order.status === "completed" ? "text-fg-muted" : order.status === "reversed" ? "text-fg-subtle" : "text-danger"}>
              {t(`detail.orders.status.${order.status}`)}
              {order.error && <div className="text-[11px] text-fg-subtle">{order.error}</div>}
            </Td>
            <Td className="text-right tabular-nums text-fg-muted">{formatMoney(order.amountMicros, currency)}</Td>
          </tr>
        ))}
      </Table>
    </Card>
  );
}
